<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Networks</h1>
        <div v-if="organizationIds.length > 1" class="mt-2">
          <label for="organization-select" class="form-label">
            Organization:
          </label>
          <b-form-select
            id="organization-select"
            v-model="selectedOrganizationId"
            :options="organizationOptions"
            class="w-auto"
            @change="onOrganizationChange"
          />
        </div>
      </b-col>
    </b-row>
    <b-row class="overflow-auto">
      <b-col class="px-sm-3 px-0">
        <b-table
          hover
          no-local-sorting
          small
          selectable
          select-mode="single"
          :fields="fields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onRowClicked"
        >
          <template #cell(name)="row">
            {{ row.item.name }}
            <span
              class="status-dot"
              :class="row.item.status === 'active' ? 'status-active' : 'status-inactive'"
              v-b-tooltip.hover="row.item.status === 'active' ? 'Active' : 'Inactive'"
            ></span>
          </template>
          <template #cell(fulfillment_type)="row">
            {{ row.item.fulfillment_type | capitalize }}
          </template>
          <template #cell(schedule)="row">
            <span class="text-nowrap">
              {{ formatTime(row.item.cutoff_time) }} / {{ formatTime(row.item.fulfillment_time) }}
            </span>
          </template>
          <template #cell(contact)="row">
            <div v-if="row.item.contact_name || row.item.contact_phone || row.item.contact_email">
              <div v-if="row.item.contact_name">{{ row.item.contact_name }}</div>
              <small v-if="row.item.contact_phone" class="text-muted d-block">
                {{ row.item.contact_phone }}
              </small>
              <small v-if="row.item.contact_email" class="text-muted d-block">
                {{ row.item.contact_email }}
              </small>
            </div>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell(delivery_instructions)="row">
            <span
              v-if="row.item.delivery_instructions"
              v-b-tooltip.hover.left="row.item.delivery_instructions"
              class="delivery-instructions"
            >
              {{ truncate(row.item.delivery_instructions, 30) }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <pagination-handler v-if="data.length" :query-data="queryFilters" />
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import tableMixin from '../../mixins/TableMixin';
import NetworksAPI from '../../services/NetworksAPI';
import OrganizationsAPI from '../../services/OrganizationsAPI';

export default {
  name: 'Networks',
  mixins: [tableMixin],
  data() {
    return {
      selectedOrganizationId: null,
      organizations: [],
      fields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
        },
        {
          key: 'hostname',
          label: 'Hostname',
          sortable: true,
        },
        {
          key: 'company_id',
          label: 'Company ID',
          sortable: true,
        },
        {
          key: 'fulfillment_type',
          label: 'Type',
          sortable: true,
        },
        {
          key: 'schedule',
          label: 'Cutoff / Fulfillment',
          sortable: false,
        },
        {
          key: 'contact',
          label: 'Contact',
          sortable: false,
        },
        {
          key: 'delivery_instructions',
          label: 'Delivery Instructions',
          sortable: false,
        },
      ],
      data: [],
    };
  },
  computed: {
    ...mapGetters('session', ['organizationAdmin', 'organizationIds']),
    currentOrganizationId() {
      return this.selectedOrganizationId ||
        (this.organizationIds.length > 0 ? this.organizationIds[0] : null);
    },
    organizationOptions() {
      return this.organizationIds.map((id) => {
        const org = this.organizations.find((o) => o.id === id);
        return {
          value: id,
          text: org ? org.name || `Organization ${id}` : `Organization ${id}`,
        };
      });
    },
  },
  created() {
    if (!this.organizationAdmin) {
      this.$router.push('/');
      return;
    }
    if (this.organizationIds.length > 0) {
      [this.selectedOrganizationId] = this.organizationIds;
    }
    this.loadOrganizations();
  },
  methods: {
    async loadOrganizations() {
      if (this.organizationIds.length <= 1) {
        return;
      }
      try {
        const response = await OrganizationsAPI.getOrganizations(this.organizationIds);
        this.organizations = response.data.data || response.data;
      } catch (error) {
        try {
          const response = await OrganizationsAPI.getOrganizationsByString(this.organizationIds);
          this.organizations = response.data.data || response.data;
        } catch (stringError) {
          try {
            const promises = this.organizationIds.map((id) =>
              OrganizationsAPI.getOrganization(id)
            );
            const responses = await Promise.all(promises);
            this.organizations = responses.map(
              (response) => response.data.data || response.data
            );
          } catch (individualError) {
            this.organizations = [];
          }
        }
      }
    },
    onOrganizationChange() {
      this.queryFilters.page = 1;
      this.loadResults();
    },
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? `${text.substring(0, length)}...` : text;
    },
    formatTime(time) {
      if (!time) return '-';
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setUTCHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    onRowClicked(item) {
      this.$router.push({
        path: `/networks/${item.id}`,
        query: { organization_id: this.currentOrganizationId },
      });
    },
    loadResults() {
      if (!this.currentOrganizationId) {
        return;
      }

      this.setRequestData();

      const searchParams = { ...this.queryFilters };

      return NetworksAPI.getNetworks(this.currentOrganizationId, searchParams)
        .then((response) => {
          // Handle both direct pagination and meta-wrapped pagination
          const data = {
            data: response.data || response,
            total: response.total || response.meta?.total || 0,
            from: response.from || response.meta?.from || 0,
            to: response.to || response.meta?.to || 0,
          };
          this.setResponseData(data);
        })
        .catch((error) => alert(error));
    },
  },
};
</script>

<style scoped>
.delivery-instructions {
  cursor: help;
  border-bottom: 1px dotted #6c757d;
}
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
  vertical-align: middle;
}
.status-active {
  background-color: #28a745;
}
.status-inactive {
  background-color: #dc3545;
}
::v-deep .table-hover tbody tr {
  cursor: pointer;
}
</style>
